import type { Task, TaskId, Subtask, SubtaskId, EventId } from "../../../shared/types";
import { createSubtask, createTask } from "../../../shared/types";
import { localTaskSchema } from "../../../shared/schemas/local.schema";

const ARCHIVE_DAYS = 7;

export function migrateTask(raw: unknown): Task {
  const parsed = localTaskSchema.safeParse(raw);
  if (!parsed.success) {
    return createTask("Tâche récupérée", "personal");
  }
  const t = parsed.data;
  return {
    id: t.id as TaskId,
    title: t.title,
    category: t.category,
    status: t.status,
    priority: t.priority,
    createdAt: t.createdAt,
    dueDate: t.dueDate,
    notes: t.notes,
    tags: t.tags,
    subtasks: t.subtasks.map((s) => ({
      ...s,
      id: s.id as SubtaskId,
    })),
    archived: t.archived,
    completedAt: t.completedAt ?? null,
    recurrence: t.recurrence,
    eventId: (t.eventId as EventId) ?? null,
  };
}

export function updateTaskInArray(
  tasks: Task[],
  id: TaskId,
  updates: Partial<
    Pick<Task, "title" | "status" | "dueDate" | "priority" | "notes" | "tags" | "eventId">
  >,
): Task[] {
  return tasks.map((t) => (t.id === id ? { ...t, ...updates } : t));
}

export function deleteTaskFromArray(tasks: Task[], id: TaskId): Task[] {
  return tasks.filter((t) => t.id !== id);
}

export function cycleTaskStatus(tasks: Task[], id: TaskId): Task[] {
  const statusCycle = {
    todo: "doing" as const,
    doing: "done" as const,
    done: "todo" as const,
  };
  return tasks.map((t) => {
    if (t.id !== id) return t;
    const nextStatus = statusCycle[t.status];
    const completedAt = nextStatus === "done" ? Date.now() : null;
    return { ...t, status: nextStatus, completedAt };
  });
}

export function addSubtaskToTask(
  tasks: Task[],
  taskId: TaskId,
  title: string,
): Task[] {
  const sub = createSubtask(title);
  return tasks.map((t) =>
    t.id === taskId ? { ...t, subtasks: [...t.subtasks, sub] } : t,
  );
}

export function toggleSubtaskInTask(
  tasks: Task[],
  taskId: TaskId,
  subtaskId: SubtaskId,
): Task[] {
  return tasks.map((t) =>
    t.id === taskId
      ? {
          ...t,
          subtasks: t.subtasks.map((s: Subtask) =>
            s.id === subtaskId ? { ...s, done: !s.done } : s,
          ),
        }
      : t,
  );
}

export function deleteSubtaskFromTask(
  tasks: Task[],
  taskId: TaskId,
  subtaskId: SubtaskId,
): Task[] {
  return tasks.map((t) =>
    t.id === taskId
      ? {
          ...t,
          subtasks: t.subtasks.filter((s: Subtask) => s.id !== subtaskId),
        }
      : t,
  );
}

export function archiveTaskInArray(tasks: Task[], id: TaskId): Task[] {
  return tasks.map((t) => (t.id === id ? { ...t, archived: true } : t));
}

export function autoArchiveTasks(tasks: Task[]): {
  tasks: Task[];
  changed: boolean;
} {
  const cutoff = Date.now() - ARCHIVE_DAYS * 24 * 60 * 60 * 1000;
  let changed = false;
  const updated = tasks.map((t) => {
    if (t.status === "done" && !t.archived) {
      // Utiliser completedAt si disponible, sinon fallback sur createdAt
      const doneAt = t.completedAt ?? t.createdAt;
      if (doneAt < cutoff) {
        changed = true;
        return { ...t, archived: true };
      }
    }
    return t;
  });
  return { tasks: updated, changed };
}

export function reorderTaskInArray(
  tasks: Task[],
  taskId: TaskId,
  newIndex: number,
): Task[] | null {
  const index = tasks.findIndex((t) => t.id === taskId);
  if (index === -1 || newIndex < 0 || newIndex >= tasks.length) return null;
  const newTasks = [...tasks];
  const [task] = newTasks.splice(index, 1);
  newTasks.splice(newIndex, 0, task);
  return newTasks;
}
